import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { AvailableQriTerminal, BindQriApi, BindQriAssignment, BindQriFilters } from '../../core/bind-qri-api';
import { PageNavigation } from '../../shared/page-navigation/page-navigation';

@Component({
  selector: 'app-bind-qri',
  imports: [CommonModule, FormsModule, PageNavigation],
  templateUrl: './bind-qri.html',
  styleUrl: './bind-qri.css'
})
export class BindQri implements OnInit {
  rows: BindQriAssignment[] = [];
  filters: BindQriFilters = { idCaja: '', idQr: null, terminalId: '' };
  private appliedFilters = { ...this.filters };
  page = 0;
  size = 10;
  total = 0;
  readonly pageSizes = [10, 25, 50, 100];
  tableLoading = false;
  message = '';
  error = '';
  modalOpen = false;
  available: AvailableQriTerminal[] = [];
  selectedTerminalId: number | null = null;
  terminalsLoading = false;
  saving = false;
  dialogError = '';
  deleting?: BindQriAssignment;
  private listRequest?: Subscription;
  private loadVersion = 0;

  constructor(
    private readonly api: BindQriApi,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void { this.load(); }

  get totalPages(): number { return Math.max(1, Math.ceil(this.total / this.size)); }

  load(): void {
    const version = ++this.loadVersion;
    this.listRequest?.unsubscribe();
    this.tableLoading = true;
    this.error = '';
    this.listRequest = this.api.list(this.page, this.size, this.appliedFilters)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => {
        if (version === this.loadVersion) this.tableLoading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: response => {
          this.rows = response.content;
          this.total = response.totalElements;
          if (this.page >= this.totalPages) {
            this.page = this.totalPages - 1;
            this.load();
          }
          this.cdr.markForCheck();
        },
        error: error => {
          this.rows = [];
          this.total = 0;
          this.error = this.errorMessage(error, 'No se pudieron cargar las asignaciones QRI.');
        }
      });
  }

  applyFilters(): void {
    this.appliedFilters = { ...this.filters };
    this.page = 0;
    this.load();
  }

  clearFilters(): void {
    this.filters = { idCaja: '', idQr: null, terminalId: '' };
    this.applyFilters();
  }

  changePage(page: number): void {
    if (this.tableLoading || page < 0 || page >= this.totalPages || page === this.page) return;
    this.page = page;
    this.load();
  }

  changeSize(): void { this.page = 0; this.load(); }

  openAssign(): void {
    if (this.modalOpen || this.saving || this.terminalsLoading) return;
    this.message = '';
    this.modalOpen = true;
    this.selectedTerminalId = null;
    this.dialogError = '';
    this.available = [];
    this.terminalsLoading = true;
    this.api.availableTerminals()
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => {
        this.terminalsLoading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: terminals => { this.available = terminals; this.cdr.markForCheck(); },
        error: error => {
          this.dialogError = this.errorMessage(error, 'No se pudieron cargar las terminales disponibles.');
        }
      });
  }

  assign(): void {
    if (this.saving || this.terminalsLoading || this.selectedTerminalId === null) return;
    this.saving = true;
    this.dialogError = '';
    this.api.assign(this.selectedTerminalId)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => {
        this.saving = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.modalOpen = false;
          this.message = 'Caja y QR BIND asignados correctamente.';
          this.load();
        },
        error: error => {
          this.dialogError = this.errorMessage(error, 'No se pudo asignar el QR a la terminal.');
        }
      });
  }

  openDelete(row: BindQriAssignment): void {
    if (this.saving) return;
    this.message = '';
    this.deleting = row;
    this.dialogError = '';
  }

  confirmDelete(): void {
    if (!this.deleting || this.saving) return;
    this.saving = true;
    this.dialogError = '';
    this.api.delete(this.deleting.id)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => {
        this.saving = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.deleting = undefined;
          this.message = 'Asignación eliminada. La terminal está disponible para asignar un nuevo QR.';
          this.load();
        },
        error: error => {
          this.dialogError = this.errorMessage(error, 'No se pudo eliminar la asignación.');
        }
      });
  }

  closeModal(): void {
    if (this.saving || this.terminalsLoading) return;
    this.modalOpen = false;
    this.deleting = undefined;
    this.dialogError = '';
  }

  private errorMessage(error: unknown, fallback: string): string {
    const body = (error as { error?: { status?: unknown; detail?: string; message?: string;
      errors?: Array<{ description?: string; message?: string }> } })?.error;
    const descriptions = body?.errors?.map(item => item.description ?? item.message).filter(Boolean);
    if (descriptions?.length) return descriptions.join('\n');
    return (typeof body?.status === 'string' ? body.status : undefined) ?? body?.detail ?? body?.message ?? fallback;
  }
}
